import { BasicButton, PrimaryButton } from '../../../../widgets/buttons';
import { PageHeader } from '../../../../widgets/pageHeader';
import { removeNotification, showInfoNotification, showNotification } from '../../../../widgets/notificationToaster';
import { Section } from '../../../../widgets/section';
import { TableContentLoader } from '../../../../widgets/table';
import { useInfiniteScroll } from '../../../../widgets/hooks/useInfiniteScroll';
import { VerticalLayout } from '../../../../widgets/layouts';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { debounce } from 'lodash';
import React, { FC, useCallback, useEffect, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, usePaginationFragment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import Loader from 'src/components/widgets/loader';
import { Center } from 'src/shared/globals';
import { backofficeUrl, T } from 'src/shared/helpers';
import styled from 'styled-components';
import { PaletteColorInput } from '../__generated__/paletteColorsPageSaveMutation.graphql';
import { PaletteInput } from '../__generated__/paletteColorsPageUpdateMutation.graphql';
import { pantoneLibraryPage_colors$key } from './__generated__/pantoneLibraryPage_colors.graphql';
import { pantoneLibraryPage_colorsQuery } from './__generated__/pantoneLibraryPage_colorsQuery.graphql';
import { pantoneLibraryPage_palettesWithColorsQuery } from './__generated__/pantoneLibraryPage_palettesWithColorsQuery.graphql';
import { pantoneLibraryPageImportDefaultPaletteMutation } from './__generated__/pantoneLibraryPageImportDefaultPaletteMutation.graphql';
import { PantoneLibraryContent } from './pantoneLibraryContent';

const DescriptionContainer = styled.span`
    display: flex;
    flex-direction: column;
`;

interface PantoneLibraryProps {
    isDialog?: boolean;
    initialPalette?: PaletteInput;
    onPaletteChange?: (palette: PaletteInput) => void;
    scrollContainer?: React.MutableRefObject<HTMLDivElement | null>;
    onAddColors?: (colors: PaletteColorInput[]) => void;
    onBackClick?: () => void;
    singleSelection?: boolean;
}

const pantoneLibraryPageColorsQuery = graphql`
    fragment pantoneLibraryPage_colors on Query
    @refetchable(queryName: "pantoneLibraryPageColorsQuery")
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 70 }
        after: { type: "String", defaultValue: "1" }
        search: { type: "String", defaultValue: null }
        id: { type: "String", defaultValue: null }
        paletteTypeID: { type: "Int", defaultValue: 1 }
        onlyDefaultColors: { type: "Boolean", defaultValue: null }
        orderBy: { type: "String", defaultValue: "DESC" }
        useConversionTable: { type: "Boolean", defaultValue: null }
    ) {
        paletteColors(
            first: $first
            after: $after
            search: $search
            id: $id
            paletteTypeID: $paletteTypeID
            onlyDefaultColors: $onlyDefaultColors
            orderBy: $orderBy
            useConversionTable: $useConversionTable
        ) @connection(key: "pantoneColorsQuery_paletteColors") {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                cursor
                node {
                    id
                    colorID
                    name
                    rgbHexCode
                    cmykHexCode
                    spotColorName
                }
            }
        }
    }
`;

const PantoneLibraryPage: FC<PantoneLibraryProps> = ({
    isDialog = false,
    initialPalette,
    onPaletteChange,
    scrollContainer,
    onAddColors,
    onBackClick,
    singleSelection = false
}) => {
    const [search, setSearch] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const navigate = useNavigate();

    const paletteQuery = useLazyLoadQuery<pantoneLibraryPage_palettesWithColorsQuery>(
        graphql`
            query pantoneLibraryPage_palettesWithColorsQuery {
                palettesWithColors {
                    edges {
                        node {
                            id
                            name
                            useConversionTable
                            paletteTypeID
                            isZakekeDefault
                        }
                    }
                }
            }
        `,
        {},
        { fetchPolicy: 'network-only' }
    );

    const colorsQuery = useLazyLoadQuery<pantoneLibraryPage_colorsQuery>(
        graphql`
            query pantoneLibraryPage_colorsQuery($id: String, $search: String, $onlyDefaultColors: Boolean, $orderBy: String, $useConversionTable: Boolean) {
                ...pantoneLibraryPage_colors
                    @arguments(id: $id, search: $search, onlyDefaultColors: $onlyDefaultColors, orderBy: $orderBy, useConversionTable: $useConversionTable)
            }
        `,
        { id: null, search: searchFilter || null, onlyDefaultColors: null, orderBy: 'DESC', useConversionTable: true },
        { fetchPolicy: 'store-or-network' }
    );

    const { data, loadNext, refetch, hasNext, isLoadingNext } = usePaginationFragment<pantoneLibraryPage_colorsQuery, pantoneLibraryPage_colors$key>(
        pantoneLibraryPageColorsQuery,
        colorsQuery
    );

    // const [paletteId, setPaletteId] = useState('guid://Zakeke/Palette/FullLibrary');
    const [selectedPalette, setSelectedPalette] = useState<PaletteInput | null>();
    const [currentColors, setCurrentColors] = useState<PaletteColorInput[]>(data?.paletteColors?.edges?.map((edge) => edge.node) || []);

    const [colorsToImport, setColorsToImport] = useState<PaletteColorInput[]>([]);
    const [isPending, startTransition] = useTransition();

    useEffect(() => {
        startTransition(() => {
            refetchItems();
        });
    }, [searchFilter, selectedPalette?.id]);

    const refetchItems = () => {
        refetch(
            {
                id:
                    selectedPalette?.id === 'guid://Zakeke/Palette/FullLibrary' || selectedPalette?.id === 'guid://Zakeke/Palette/DefaultLibrary'
                        ? null
                        : selectedPalette?.id,
                search: searchFilter || null,
                onlyDefaultColors: selectedPalette?.id === 'guid://Zakeke/Palette/DefaultLibrary' ? true : null,
                orderBy: 'DESC',
                useConversionTable: true
            },
            { fetchPolicy: 'network-only' }
        );
    };

    useInfiniteScroll(
        () =>
            startTransition(() => {
                loadNext(70);
            }),
        hasNext,
        scrollContainer?.current, // Pass the scroll container element
        isLoadingNext
    );

    useEffect(() => {
        setCurrentColors(data?.paletteColors?.edges?.map((edge) => edge.node) || []);
    }, [data]);

    const handleSelectedColorsChange = (colors: PaletteColorInput[]) => {
        setColorsToImport(colors);
    };

    const handleSelectedPaletteChange = (palette: PaletteInput | null) => {
        setSelectedPalette(palette);
    };

    const setSearchDebounced = useCallback(
        debounce((filter: string) => {
            setSearchFilter(filter);
        }, 1000),
        []
    );

    const handleSearchColor = (search: string) => {
        setSearch(search);
        setSearchDebounced(search);
    };

    const [importPalette, isImportingPalette] = useMutation<pantoneLibraryPageImportDefaultPaletteMutation>(graphql`
        mutation pantoneLibraryPageImportDefaultPaletteMutation($id: ID!) {
            importDefaultPalette(id: $id)
        }
    `);

    const handleImportPaletteClick = () => {
        if (selectedPalette && selectedPalette.id) {
            const importPaletteNotification = showInfoNotification({
                message: T._(`Importing Zakeke default Palette... please don't exit from page.`, 'Admin'),
                dismiss: undefined
            });

            importPalette({
                variables: {
                    id: selectedPalette.id
                },
                onCompleted: () => {
                    removeNotification(importPaletteNotification);
                    showNotification({
                        type: 'success',
                        message: T._('Palette imported.', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                    navigate(backofficeUrl('/settings/color-palette-settings?palette=imported'));
                },
                onError: () => {
                    removeNotification(importPaletteNotification);
                    showNotification({
                        type: 'danger',
                        message: T._('Error importing palette.', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                }
            });
        }
    };

    const DescriptionPage = () => {
        return (
            <DescriptionContainer>
                <span>{T._('Select a Pantone palette to view its colors. You can import individual colors or the entire palette.', 'Admin')}</span>
                <span>{T._('Zakeke default colors: 20.091', 'Admin')}</span>
            </DescriptionContainer>
        );
    };

    return (
        <VerticalLayout>
            <PageHeader
                title={T._('Pantone palettes', 'Admin')}
                description={<DescriptionPage />}
                backLabel={T._('Back', 'Admin')}
                isBackVisible={true}
                onBackClick={onBackClick}
                actions={
                    <>
                        {colorsToImport.length === 0 ? (
                            <ZTooltip title={T._('Select at least one color to add', 'Admin')} addContainer={true}>
                                <BasicButton
                                    size='medium'
                                    disabled={colorsToImport.length === 0}
                                    onClick={() => {
                                        onAddColors?.(colorsToImport);
                                    }}
                                >
                                    {T._(singleSelection ? `Add selected color` : `Add selected colors`, 'Admin')}
                                </BasicButton>
                            </ZTooltip>
                        ) : (
                            <BasicButton
                                size='medium'
                                disabled={colorsToImport.length === 0}
                                onClick={() => {
                                    onAddColors?.(colorsToImport);
                                }}
                            >
                                {T._(singleSelection ? `Add selected color` : `Add selected colors`, 'Admin')}
                            </BasicButton>
                        )}
                        {selectedPalette?.isZakekeDefault && (
                            <PrimaryButton
                                size='medium'
                                onClick={() => {
                                    handleImportPaletteClick();
                                }}
                            >
                                {T._('Import entire palette', 'Admin')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />
            <Section>
                <PantoneLibraryContent
                    // incomingPalette={incomingPalette}
                    onSelectedColorsChange={handleSelectedColorsChange}
                    // onSearchColor={handleSearchColor}
                    currentColors={currentColors}
                    // currentPalettes={paletteQuery.palettesWithColors}
                    // onSelectedPaletteChange={handleSelectedPaletteChange}
                    singleSelection={singleSelection}
                    initialSearchValue={search}
                />

                {isPending ||
                    (hasNext && isLoadingNext && (
                        <Center style={{ marginTop: '15px' }}>
                            <Loader />
                        </Center>
                    ))}

                {isPending && <TableContentLoader />}
            </Section>
        </VerticalLayout>
    );
};

export default PantoneLibraryPage;
